/**
 * @fileoverview A plant's stage on its card: one pill, "Maturity ⌄", in the
 * stage's own colours, and the sheet that pressing it opens.
 *
 * The pill says the stage alone (owner, 2026-10-15, "go with number one"): the
 * bed and the pill share one line, and "Maturity since Sep 14" left the bed
 * as "My…" on a phone. The day it began is the pill's title and spoken name,
 * and the sheet's history lists it.
 *
 * A plant's stage is its stage history (2026-10-03): each stage it reached and
 * the day it began. The server decides the stage now and the history's rows
 * (W24 Job F: plant.stage and plant.stage_history, services/grow/currentStage);
 * this line draws them as sent and works nothing out. Photos no longer carry a
 * stage, so this is the one place a stage is changed. The sheet records a new
 * stage from a day (the garden's today unless said), and lists the history as
 * the server orders it, newest first, each stage note with × to take it back (a
 * photo-only plant's old photograph stages are listed without one).
 *
 * The pill is the shared Pill (ui/Pill), drawn as a 6px-cornered tag with a down-chevron (B136), and the one button for changing the
 * stage (there is no separate Change): its colours are the stage's badge
 * colours (the gardener's own, from their stage list), with a chevron. An
 * estimate is a paler pill with a dashed edge, as pencilled in. With no stage yet it is a dashed "+ Set stage". On an
 * archived plant it is the same pill without the chevron, and it does nothing.
 * Its accessible name is the full line ("Maturity since Sep 14") and "change
 * stage". A point the plant ended at (the server's ended: Last Harvest,
 * Expired) has the full line "Harvested Oct 14", not "since" it.
 *
 * Grow's automatic stages (2026-10-05) join the history as estimates: the
 * pill is pencilled in and its full line reads "Seedling since Mar 4 ·
 * estimated", and in the sheet each
 * estimate says so and can be confirmed (recorded as it is) or corrected
 * (its stage and day put in the form to change before saving). A correction
 * stops the automatic stages for the plant; while it does, the sheet says so
 * and offers "Automatic again", which lets them carry on from the newest
 * entry, removing nothing.
 *
 * A plant in the Seed vault (D69, the server's move_out) is asked "Which bed did they go into?" before a stage that moves
 * it out is recorded (features/seed-orders WhichBedSheet): Transplanting or later needs a bed; Seeding offers "Indoors
 * (stays in the Seed vault)" or a bed. The bed goes with the stage (POST /stage-marks { locationId }) and the server
 * moves the plant there first. Any other plant, or stage, is asked nothing.
 *
 * The card may open the sheet itself (sheetOpen, onSheetOpenChange): a "Not
 * sure" plant's "Planted yet? · Already growing" (PlantingDatesLine) opens it
 * with growing, offering the stages past Planning.
 *
 * The stages are a row of pills in their own colours (the chosen one
 * ringed), as the quick notes are, ending in a dashed "+ New stage": it turns
 * into a name box with Add and Cancel in place, and Add makes the stage
 * (POST /stages, the new-stage colour), chooses it and puts it in the row.
 * Renaming, recolouring, hiding and reordering stay in Profile > Labels.
 *
 * Where: the plant card (PlantCard), on the bed's line (the bed's name gives
 * way when the two do not fit); its sheet is also opened by the Not sure
 * plant's "Planted yet?".
 * API: POST /stages { name, colorKey } (createStage, "+ New stage");
 * POST /stage-marks/:plantObjId { stageId, date } (addStageMark),
 * DELETE /stage-marks/:plantObjId/:markId (removeStageMark), POST
 * /stage-marks/:plantObjId/:markId/auto (autoAgain); then the plant is read
 * again (updateSinglePlant: GET /plants?plantObjId=).
 * Settings: the gardener's "Move my plants' stages for me" (no estimates
 * when off); a plant's stage_paused after a correction.
 * Words (en/es): plants.stage.* (line, lineEstimated, lineOn, lineOnEstimated,
 * none, pillLabel, set,
 * sheetTitle, label, since, save, paused, autoAgain, history, estimated, newStage,
 * newStageName, newStageAdd, newStageCancel, newStageFailed,
 * confirm, correct, confirmEntry, correctEntry, removeEntry, failed); sitewide.cancel, sitewide.saving (Button's loading word).
 * The sheet's foot is an ActionRow (B111): Cancel on the left, Save on the right with the spinner while it records; the
 * "+ New stage" box has Cancel and Add in an ActionRow too. The history rows' Confirm and Correct are unchanged (card B143).
 * The day is the garden's own (useGardenPostcode, the server's, from the
 * garden's time zone; architecture audit A31), never the phone's: the box
 * starts on it and goes no later; a day still to come is recorded as the
 * garden's today by the server (routes/stageMarks), not cut here.
 * Failures (B89, the error standard): one on our side (D31, a 500 or no
 * answer) is reported to Sentry and its toast says the standard sentence
 * (lib/serverFailure), which names hello@gardengameplan.com; a refusal the
 * server explains (STAGE_NOT_ONE_OF_YOURS, BED_REQUIRED...) says the server's
 * words in the reader's language (lib/errorMessage serverMessage); anything
 * else says plants.stage.failed or newStageFailed, which never ask to try
 * again. One helper, failureText, words all four (record, take back,
 * automatic again, new stage).
 *
 * @module PlantStageLine
 */

import React from 'react';
import { X, Plus } from 'lucide-react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Pill } from '@/shared/components/ui/Pill';
import { Input } from '@/shared/components/ui/input';
import { useQueryClient } from '@tanstack/react-query';
import { themeForColorKey } from '@/shared/theme/colorPalette';
import { createStage } from '@/shared/api/stages';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { LOCATIONS_QUERY_KEY, STAGES_QUERY_KEY } from '@/config/tunables/ui';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { useStagesContext } from '@/shared/hooks/useStagesContext';
import { selectableStages } from '@/shared/lib/stages';
import { addStageMark, autoAgain, removeStageMark } from '@/shared/api/stageMarks';
import { formatDate, parseDate } from '@/shared/utils/dateUtils';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import type { Plant } from '@/shared/types/plant';
import type { StageRecord } from '../../lib/plantStage';
import { serverFailureText } from '@/shared/lib/serverFailure';
import { serverMessage } from '@/shared/lib/errorMessage';
import { plantName } from '@/shared/lib/plantName';
import { askWhichBed, stageAskOf } from '@/features/seed-orders/lib/whichBed';

/** What the line is given. */
interface PlantStageLineProps {
  /** The plant, with its stage now (stage), its history's rows (stage_history) and its stage notes (stage_marks, for "Automatic again"). */
  plant: Plant;
  /** False on an archived plant: its stage is shown, not changed. */
  editable: boolean;
  /** Whether the sheet is open, when the card holds that; the line holds it itself otherwise. */
  sheetOpen?: boolean;
  /** Opens or closes the sheet, when the card holds whether it is open. */
  onSheetOpenChange?: (open: boolean) => void;
  /** Opened from "Already growing": Planning is not offered. */
  growing?: boolean;
}

/**
 * The words for a failed stage request (B89): the standard sentence for a failure on our side (reported to Sentry
 * under where), the server's own explanation of a refusal, else the fallback.
 *
 * @param error - Whatever the request threw.
 * @param where - The step, for the Sentry report ("plantStage.record").
 * @param fallback - The fallback words, already translated (never "try again").
 * @returns The toast's title.
 */
function failureText(error: unknown, where: string, fallback: string): string {
  return serverFailureText(error, where) ?? serverMessage(error) ?? fallback;
}

/**
 * The stage's look on the card (B136, design.md rev 12): a rounded-square tag with 6px corners, not a round pill, so with its
 * select-style down-chevron it reads as "tap to change".
 */
const STAGE_TAG = 'min-h-7 rounded-md';

/**
 * The pill, and the sheet it opens.
 *
 * @param props - PlantStageLineProps.
 */
export const PlantStageLine: React.FC<PlantStageLineProps> = ({ plant, editable, sheetOpen, onSheetOpenChange, growing = false }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const limits = useGrowLimits();
  const { toast } = useToast();
  const { updateSinglePlant } = usePlantsContext();
  const { stages } = useStagesContext();
  // Planning too: a plant added before it is sown is Planning; not for one the gardener says is already growing.
  const stageList = React.useMemo(() => selectableStages(stages, { planning: !growing }), [stages, growing]);
  /** The stage it is at now, as the server sends it (null: nothing recorded). */
  const current = plant.stage;
  /** The garden's own day, the server's ('' until it is known). */
  const { date: today } = useGardenPostcode();
  const sinceId = `stage-since-${plant.plant_obj_id}`;

  const [ownOpen, setOwnOpen] = React.useState(false);
  const open = sheetOpen ?? ownOpen;
  /**
   * Opens or closes the sheet: the card's, when it holds whether it is open.
   *
   * @param next - Whether it is to be open.
   */
  const setOpen = (next: boolean): void => {
    if (onSheetOpenChange) onSheetOpenChange(next);
    else setOwnOpen(next);
  };
  const [stageId, setStageId] = React.useState<number | null>(null);
  const [since, setSince] = React.useState(today);
  /** True while anything waits on the server (a stage recording or a new stage being made): the sheet's controls wait. */
  const [busy, setBusy] = React.useState(false);
  /** True while the new stage is being made, so Add shows the spinner and Save does not. */
  const [adding, setAdding] = React.useState(false);
  const queryClient = useQueryClient();
  /** The new stage's name while "+ New stage" is open as a box; null while it is a pill. */
  const [newName, setNewName] = React.useState<string | null>(null);
  // Each opening starts from the stage it is at, and the garden's today.
  React.useEffect(() => {
    if (!open) return;
    setStageId(current?.id ?? stageList[0]?.stage_id ?? null);
    setSince(today);
    setNewName(null);
    // Read as the sheet opens, not on every change while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** A day as the line says it, in the app's language: "Jul 10", "10 jul". */
  const day = (on: string | undefined): string => (on ? formatDate(parseDate(on).setLocale(language)) : '');

  /**
   * Records a stage from a day and reads the plant again.
   *
   * @param id - The stage.
   * @param on - Its first day, YYYY-MM-DD; the server records a day still to come as the garden's today.
   * @returns Whether it was recorded.
   */
  const record = async (id: number, on: string): Promise<boolean> => {
    // D69: a plant in the Seed vault leaving it (the server's move_out: Transplanting or later needs a bed; Seeding asks
    // Indoors or a bed) is asked "Which bed did they go into?" first; closing the question records nothing.
    const asks = stageAskOf(plant.move_out, id);
    let locationId: number | null = null;
    if (asks) {
      const answer = await askWhichBed({ sub: stageList.find((stage) => stage.stage_id === id)?.name ?? null, indoors: asks === 'indoors' });
      if (!answer) return false;
      locationId = 'bedId' in answer ? answer.bedId : null;
    }
    setBusy(true);
    try {
      await addStageMark(plant.plant_obj_id, id, on, locationId);
      await updateSinglePlant(String(plant.plant_obj_id));
      // Moved out of the vault: the beds' plant lists changed too.
      if (locationId) void queryClient.invalidateQueries({ queryKey: [LOCATIONS_QUERY_KEY] });
      return true;
    } catch (error) {
      toast({ title: failureText(error, 'plantStage.record', t('plants.stage.failed')), variant: 'destructive' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Makes the stage named in the box, chooses it, and puts it in the row (the stages are read again). */
  const addStage = async (): Promise<void> => {
    const name = (newName ?? '').trim();
    if (!name) return;
    setBusy(true);
    setAdding(true);
    try {
      const id = await createStage(name);
      await queryClient.invalidateQueries({ queryKey: [STAGES_QUERY_KEY] });
      setStageId(id);
      setNewName(null);
    } catch (error) {
      toast({ title: failureText(error, 'plantStage.newStage', t('plants.stage.newStageFailed')), variant: 'destructive' });
    } finally {
      setBusy(false);
      setAdding(false);
    }
  };

  /** Records the stage chosen, from the day given, and closes the sheet. */
  const save = async (): Promise<void> => {
    if (stageId === null || !since) return;
    if (await record(stageId, since)) setOpen(false);
  };

  /**
   * Takes one entry back out of the history.
   *
   * @param markId - The stage note's id.
   */
  const takeBack = async (markId: number): Promise<void> => {
    setBusy(true);
    try {
      await removeStageMark(plant.plant_obj_id, markId);
      await updateSinglePlant(String(plant.plant_obj_id));
    } catch (error) {
      toast({ title: failureText(error, 'plantStage.takeBack', t('plants.stage.failed')), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Puts an estimate's stage and day in the form, to change before saving.
   *
   * @param id - Its stage.
   * @param on - Its day.
   */
  const correct = (id: number, on: string): void => {
    setStageId(id);
    setSince(on);
    document.getElementById(sinceId)?.focus();
  };

  /** "Automatic again": the automatic stages carry on from the newest entry (stage_marks come newest first). */
  const resume = async (): Promise<void> => {
    const newest = plant.stage_marks[0];
    if (!newest) return;
    setBusy(true);
    try {
      await autoAgain(plant.plant_obj_id, newest.mark_id);
      await updateSinglePlant(String(plant.plant_obj_id));
    } catch (error) {
      toast({ title: failureText(error, 'plantStage.autoAgain', t('plants.stage.failed')), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * The history's rows as the server sent them, newest first: nothing is sorted here. The server always sends the list;
   * a plant drawn from an answer without it (a test's plant) lists no rows.
   */
  const history: StageRecord[] = plant.stage_history ?? [];
  /** Whether the stage now is Grow's estimate (the server's). */
  const estimated = current?.estimated === true;
  /** The current stage as the gardener's stage list has it: its colour, and whether it ended. */
  const currentStage = current ? stages?.find((stage) => stage.stage_id === current.id) : undefined;
  /** A point the plant ended at (the server's ended: Last Harvest, Expired): "Harvested Jun 3", not "since" it (owner, 2026-10-14). */
  const ended = Boolean(currentStage?.ended);
  /** The stage's own colours: its badge, or, for an estimate, the paler fill and edge of the same colour. */
  const theme = themeForColorKey(currentStage?.color_key);
  const lineKey = ended
    ? (estimated ? 'plants.stage.lineOnEstimated' : 'plants.stage.lineOn')
    : (estimated ? 'plants.stage.lineEstimated' : 'plants.stage.line');
  const label = current
    ? (current.since ? t(lineKey, { stage: current.name, date: day(current.since) }) : current.name)
    : t('plants.stage.none');
  /** What the pill shows: the stage alone, so the bed beside it keeps its name; the full line is its title and spoken name. */
  const pillText = current ? current.name : label;

  return (
    <>
      {current ? (
        editable ? (
          <Pill
            onClick={() => setOpen(true)}
            aria-label={t('plants.stage.pillLabel', { line: label })}
            title={label}
            theme={theme}
            estimate={estimated}
            chevron
            className={STAGE_TAG}
          >
            {pillText}
          </Pill>
        ) : (
          <Pill title={label} aria-label={label} theme={theme} estimate={estimated} className={STAGE_TAG}>{pillText}</Pill>
        )
      ) : editable ? (
        <Pill tone="dashed" onClick={() => setOpen(true)} icon={<Plus />}>{t('plants.stage.set')}</Pill>
      ) : (
        <span className="text-sm text-muted-foreground">{label}</span>
      )}

      <BottomPanel open={open} onOpenChange={(next) => { if (!busy) setOpen(next); }} title={t('plants.stage.sheetTitle', { plant: plantName(plant) })} wrapTitle>
        <div className="flex flex-col gap-3">
          <div>
            <p id={`stage-label-${plant.plant_obj_id}`} className="mb-1.5 block text-xs text-muted-foreground">{t('plants.stage.label')}</p>
            <div role="radiogroup" aria-labelledby={`stage-label-${plant.plant_obj_id}`} className="flex flex-wrap gap-2">
              {stageList.map((stage) => {
                const theme = themeForColorKey(stage.color_key);
                const chosen = stage.stage_id === stageId;
                return (
                  <Pill
                    key={stage.stage_id}
                    role="radio"
                    aria-checked={chosen}
                    onClick={() => setStageId(stage.stage_id)}
                    disabled={busy}
                    theme={theme}
                    selected={chosen}
                  >
                    {stage.name}
                  </Pill>
                );
              })}
              {newName === null ? (
                <Pill tone="dashed" onClick={() => setNewName('')} disabled={busy} icon={<Plus />}>
                  {t('plants.stage.newStage')}
                </Pill>
              ) : (
                <form
                  className="flex basis-full flex-col gap-2"
                  onSubmit={(event) => { event.preventDefault(); void addStage(); }}
                >
                  <Input
                    autoFocus
                    value={newName}
                    maxLength={limits.STAGE_NAME_MAX}
                    onChange={(event) => setNewName(event.target.value)}
                    placeholder={t('plants.stage.newStageName')}
                    aria-label={t('plants.stage.newStageName')}
                    disabled={busy}
                    className="h-10 min-w-0 flex-1 rounded-full"
                  />
                  <ActionRow onCancel={() => setNewName(null)} cancelLabel={t('plants.stage.newStageCancel')} disabled={busy}>
                    <Button type="submit" loading={adding} busy="adding" disabled={busy || !newName.trim()}>
                      {t('plants.stage.newStageAdd')}
                    </Button>
                  </ActionRow>
                </form>
              )}
            </div>
          </div>
          <div className="max-w-[12rem]">
            <label htmlFor={sinceId} className="mb-1 block text-xs text-muted-foreground">{t('plants.stage.since')}</label>
            <Input
              id={sinceId}
              type="date"
              value={since}
              max={today || undefined}
              onChange={(event) => setSince(event.target.value)}
              disabled={busy}
            />
          </div>
          <ActionRow onCancel={() => setOpen(false)} disabled={busy}>
            <Button onClick={() => void save()} loading={busy && !adding} disabled={busy || stageId === null || !since}>{t('plants.stage.save')}</Button>
          </ActionRow>

          {/* A correction of theirs stopped the automatic stages: say so, and offer them back. */}
          {plant.stage_paused && plant.stage_marks.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {t('plants.stage.paused')}{' '}
              <button
                type="button"
                onClick={() => void resume()}
                disabled={busy}
                className="min-h-11 font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50"
              >
                {t('plants.stage.autoAgain')}
              </button>
            </p>
          )}

          {history.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('plants.stage.history')}</p>
              <ul className="divide-y rounded-sm border">
                {history.map((entry, index) => (
                  <li key={`${entry.on}-${entry.stage_id}-${entry.from}-${index}`} className="flex min-h-11 items-center justify-between gap-x-2 px-3 text-sm">
                    <span className="min-w-0 flex-1 py-2">
                      <span className="font-medium">{entry.name}</span>
                      <span className="text-muted-foreground"> · {day(entry.on)}</span>
                      {entry.from === 'estimate' && <span className="text-muted-foreground"> · {t('plants.stage.estimated')}</span>}
                    </span>
                    {entry.from === 'estimate' && (
                      <span className="-mr-2 flex shrink-0 items-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-11 px-2 text-primary"
                          onClick={() => void record(entry.stage_id, entry.on)}
                          aria-label={t('plants.stage.confirmEntry', { stage: entry.name, date: day(entry.on) })}
                          disabled={busy}
                        >
                          {t('plants.stage.confirm')}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-11 px-2 text-primary"
                          onClick={() => correct(entry.stage_id, entry.on)}
                          aria-label={t('plants.stage.correctEntry', { stage: entry.name, date: day(entry.on) })}
                          disabled={busy}
                        >
                          {t('plants.stage.correct')}
                        </Button>
                      </span>
                    )}
                    {entry.from === 'note' && entry.mark_id !== null && (
                      <button
                        type="button"
                        onClick={() => void takeBack(entry.mark_id as number)}
                        aria-label={t('plants.stage.removeEntry', { stage: entry.name, date: day(entry.on) })}
                        className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                        disabled={busy}
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </BottomPanel>
    </>
  );
};
